import React, { useState, useEffect } from "react";
import { GridRowModes } from "@mui/x-data-grid";
import moment from "moment";

import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";

import { ReactComponent as EditIcon } from "assets/img/svg/Edit2.svg";
import { ReactComponent as DeleteIcon } from "assets/img/svg/Delete.svg";
import { ReactComponent as CrossIcon } from "assets/img/svg/CrossIcon.svg";

import CustomDatePicker from "views/pages/manage/common/commonComponents/CustomDatePicker";
import { LastUpdatedDetails } from "views/pages/manage/employeeManagement/userProfile/helper";

import {
  failureToast,
  successToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";

import { getTestMappings, updateTestMapping } from "./helper";

const SEARCH_BY = [
  { key: "testId", value: "Test ID" },
  { key: "testDescr", value: "Description" },
];

const PAGE_SIZE = 50;

const EMPTY_SEARCH = { searchBy: "", searchByValue: "" };

const TestMappingTable = ({
  defrId,
  testMappings, // ← never replaced here; drives warning banner in parent
  setTestMappings, // ← called after add / delete to keep parent in sync
  isActionsAllowed,
  courseId, // ← required for API calls
}) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowForm, setRowForm] = useState({});
  const [editRowId, setEditRowId] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [selectedKey, setSelectedKey] = useState(SEARCH_BY[0].key);
  const [appliedSearch, setAppliedSearch] = useState(EMPTY_SEARCH);

  const [displayRows, setDisplayRows] = useState(testMappings);
  const [rowCount, setRowCount] = useState(testMappings.length);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDisplayRows(testMappings);
    setRowCount(testMappings.length);
  }, [testMappings]); // intentionally runs once

  const fetchPage = async (
    page = currentPage,
    searchPayload = appliedSearch,
  ) => {
    if (!courseId) return;

    setIsLoading(true);
    try {
      const payload = {
        courseId,
        ...(searchPayload.searchByValue && searchPayload),
      };
      const response = await getTestMappings(page, PAGE_SIZE, payload);

      if (response?.code === 200) {
        setDisplayRows(response.data?.dataList ?? []);
        setRowCount(response.data?.totalRecords ?? 0);
      } else if (Array.isArray(response)) {
        // fallback: helper returns plain array
        setDisplayRows(response);
        setRowCount(response.length);
      }
    } catch (err) {
      console.error("Failed to fetch test mappings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const onPageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchPage(newPage);
  };

  const submitSearch = (value = searchInput) => {
    const payload = {
      searchBy: value ? selectedKey : "",
      searchByValue: value,
    };
    setAppliedSearch(payload);
    setCurrentPage(0);
    fetchPage(0, payload);
  };

  const onKeyDownHandler = (e) => {
    if (e.key === "Enter" || e.keyCode === 13) submitSearch();
  };

  const handleSearchInput = (value) => {
    setSearchInput(value);
    if (value === "" && value !== appliedSearch.searchByValue) {
      submitSearch(value);
    }
  };

  const validateRow = () => {
    if (!rowForm.testId) return "Test ID required";
    if (!rowForm.testDescr) return "Description required";
    if (!rowForm.testDate) return "Test Date required";

    const duplicate = testMappings.filter(
      (r) => r.testId === rowForm.testId && r.id !== editRowId && !r.isDeleted,
    );
    if (duplicate.length > 0) return "Duplicate Test ID not allowed";

    return null;
  };

  const handleEditClick = (row) => {
    if (editRowId) return;
    setEditRowId(row.id);
    setRowForm({
      ...row,
      testDate: row.testDate ? moment(row.testDate) : null,
    });
    setRowModesModel((prev) => ({
      ...prev,
      [row.id]: { mode: GridRowModes.Edit },
    }));
  };

  const handleSaveClick = async (id) => {
    const error = validateRow();
    if (error) return failureToast(error);

    try {
      const payload = {
        id: rowForm.id,
        courseId: rowForm.courseId,
        academicTermId: rowForm.academicTermId,
        academicTermDispValue: rowForm.academicTermDispValue,
        testId: rowForm.testId,
        testDescr: rowForm.testDescr,
        testDate: rowForm.testDate,
      };

      if (defrId) {
        const res = await updateTestMapping(payload);
        if (res?.code !== 200) return;
      }

      const updater = (list) =>
        list.map((r) => (r.id === id ? { ...r, ...rowForm, isNew: false } : r));

      setDisplayRows(updater);
      setTestMappings(updater);

      successToast("Updated successfully");
      setEditRowId(null);
      setRowForm({});
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }));
    } catch (err) {
      console.error(err);
      failureToast("Update failed");
    }
  };

  const handleCancelClick = (id) => {
    const isNew = displayRows.find((r) => r.id === id)?.isNew;

    if (isNew) {
      const remover = (list) => list.filter((r) => r.id !== id);
      setDisplayRows(remover);
      setTestMappings(remover);
    }

    setEditRowId(null);
    setRowForm({});
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  const handleDeleteClick = async (id) => {
    const row = displayRows.find((r) => r.id === id);

    try {
      if (defrId) {
        await updateTestMapping({
          id: row.id,
          courseId: row.courseId,
          academicTermId: row.academicTermId,
          isDeleted: true,
        });
      }

      const remover = (list) => list.filter((r) => r.id !== id);
      setDisplayRows(remover);
      setTestMappings(remover);
      setRowCount((prev) => Math.max(0, prev - 1));
      successToast("Deleted successfully");

      // If page is now empty and we're not on page 0, go back one page
      if (displayRows.length - 1 === 0 && currentPage > 0) {
        onPageChange(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
      failureToast("Delete failed");
    }
  };

  const handleFieldChange = (key, value) => {
    setRowForm((prev) => ({ ...prev, [key]: value }));
  };

  const columns = [
    new AGridColDef("academicTermDispValue", "Term")
      .setMinWidth(80)
      .renderCellComponent((p) => p.value || "-"),

    new AGridColDef("testId", "Test ID")
      .setMinWidth(150)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <AInput
          value={rowForm.testId || ""}
          handler={(val) => handleFieldChange("testId", val)}
        />
      )),

    new AGridColDef("testDescr", "Description")
      .setMinWidth(220)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <AInput
          value={rowForm.testDescr || ""}
          handler={(val) => handleFieldChange("testDescr", val)}
        />
      )),

    new AGridColDef("testDate", "Test Date")
      .setMinWidth(180)
      .editable(true)
      .renderCellComponent((p) =>
        p.value ? new Date(p.value).toLocaleDateString() : "-",
      )
      .renderEditCellComponent(() => (
        <CustomDatePicker
          value={rowForm.testDate ? moment(rowForm.testDate) : null}
          handler={(i, date) => handleFieldChange("testDate", date)}
        />
      )),

    new AGridColDef("updatedOn", "Updated On")
      .setMinWidth(180)
      .renderCellComponent((p) =>
        p.value ? LastUpdatedDetails(p.value) : "-",
      ),

    new AGridColDef("updatedBy", "Updated By")
      .setMinWidth(100)
      .renderCellComponent((p) => p.value || "-"),
  ];

  if (isActionsAllowed) {
    columns.push(
      new AGridColDef("actions", "Actions")
        .setMinWidth(150)
        .renderCellComponent((params) => {
          const isEditing = editRowId === params.row.id;

          if (isEditing) {
            return (
              <>
                <AButton
                  size="xs"
                  onClick={() => handleSaveClick(params.row.id)}
                >
                  Save
                </AButton>
                <IconButtonWrapper
                  onClick={() => handleCancelClick(params.row.id)}
                >
                  <CrossIcon />
                </IconButtonWrapper>
              </>
            );
          }

          return (
            <>
              <IconButtonWrapper onClick={() => handleEditClick(params.row)}>
                <EditIcon />
              </IconButtonWrapper>
              <IconButtonWrapper
                onClick={() => handleDeleteClick(params.row.id)}
              >
                <DeleteIcon />
              </IconButtonWrapper>
            </>
          );
        }),
    );
  }

  return (
    <div className="w-100">
      <div
        className="d-flex align-items-center mb-2"
        style={{
          position: "relative",
          top: "-39px",
          left: "-117px",
          justifyContent: "end",
        }}
      >
        <AInput
          placeholder="Search"
          value={searchInput}
          handler={handleSearchInput}
          onKeyDown={onKeyDownHandler}
          searchIconClickHandler={() => submitSearch()}
          crossIcon={false}
          searchIcon={true}
          style={{
            width: "300px",
          }}
          customSearchIconStyle={{ left: "5px" }}
          rightCustomIconStyles={{
            fontSize: "12px",
            position: "absolute",
            bottom: "7px",
            right: "113px",
            margin: "auto",
          }}
          rightCustomIcon={
            <select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              style={{
                position: "absolute",
                bottom: "0",
                margin: "auto 0",
                background: "#FFF6E3",
                color: "#A17217",
                border: "none",
              }}
            >
              {SEARCH_BY.map(({ key, value }) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          }
        />
      </div>

      {/* ── Data grid ── */}
      <ADataGrid
        rows={displayRows}
        columns={columns}
        rowId={(row) => row.id}
        height="350px"
        rowModesModel={rowModesModel}
        loading={isLoading}
        key={JSON.stringify(displayRows)}
        rowCount={rowCount}
        nextButtonHandler={onPageChange}
        currentPage={currentPage}
      />
    </div>
  );
};

export default TestMappingTable;
